import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import { IApplicationState, AppActionCreators, MatchActionCreators, IEvaluateRecipientDonorResponse, DonorPreselectActionCreators, IAcceptDonorResponse, IRequestExmResponse, IAppState, FileActionCreators, DonorActionCreators, IAmbraDirectToViewerResponse } from '@nkr/store/index';
import { IRecipient, IDonor, ICompatibilityResult, ILkdpiResults, IDonorPreselectNote, ISerologyTest, IBaseResponse, ICenterAddress, IState as IStateItem, IContact, ISingleEpletMmRisk, IListItem } from '@nkr/models/index';
import { toast } from 'react-toastify';
import { Utility } from '@nkr/services/utility';
import { Alert, Button, Col, Row } from 'react-bootstrap';
import { WellBottomWrapper, WellTopWrapper } from './styles';
import { ActionItems } from './action-items';
import { FileDocumentType, FileDocumentTypeDescription, MortalityMatchType } from '@nkr/enums/index';
import { downloadFile } from '@nkr/services/download-file';
import RecipientNav from '../recipients/recipient-nav';
import Match from './match';
import ActionsButton from './actions-button';
import InfoRequest from './modals/info-request';
import ExmRequest from './modals/exm-request';
import DeclineDonorForMortalityRisk from './modals/decline-donor-for-mortality-risk';

interface IProps {
    centerId: number,
    recipientId: number,
    rexmId: number,
    onUpdateRecipientAlias: (alias: string) => void,
    onGetFile: (docType: FileDocumentType, donorId: number) => void
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getRexm: (centerId: number, recipientId: number, rexmId: number) => Promise<IEvaluateRecipientDonorResponse>,
    acceptDonor: (recipientId: number, donorId: number, isFullyConfident: boolean, hasMortalityRisk: boolean) => Promise<IAcceptDonorResponse>,
    declineDonorRexm: (recipientId: number, recipientCenterId: number, donorId: number, hardBlockSource: string, hardBlockReason: string, hardBlockComments: string, isGlobalDecline: boolean, hasMortalityRisk: boolean) => Promise<IBaseResponse>,
    requestInfo: (recipientId: number, donorId: number, message: string) => Promise<IBaseResponse>,
    requestExm: (recipientId: number, donorId: number, message: string, allowDuplicate: boolean, freshBloodOverride: boolean) => Promise<IRequestExmResponse>,
    downloadFile: (docType: number, lookupId: number) => Promise<any>,
    getAmbraLink: (donorId: number) => Promise<IAmbraDirectToViewerResponse>,
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    recipient: IRecipient,
    donor: IDonor,
    compatibilityResult: ICompatibilityResult,
    lkdpiResults: ILkdpiResults,
    compatiblePairLkdpiResults: ILkdpiResults,
    singleEpletMmRisk: ISingleEpletMmRisk,
    isRecipientCenter: boolean,
    recipientCenterContacts: IContact[],
    isDonorCenter: boolean,
    donorCenterContacts: IContact[],
    donorNote: IDonorPreselectNote,
    donorNotesContent: string,
    matchNote: IDonorPreselectNote,
    matchNotesContent: string,
    donorKidneyPickupAddress: ICenterAddress,
    donorKidneyShipToAddress: ICenterAddress,
    recipientKidneyPickupAddress: ICenterAddress,
    recipientKidneyShipToAddress: ICenterAddress,
    serologyTestList: ISerologyTest[],
    lookupData: {
        stateList: IStateItem[],
        hardBlockReasons: IListItem[]
    },
    doesStudyExist: number,
    centerAddress: {
        show: boolean,
        data: ICenterAddress
    },
    showSerologyTests: boolean,
    hasOutstandingOffer: boolean,
    showInfoRequest: boolean,
    hasExistingXm: boolean,
    existingXmStatus: string,
    requestExm: {
        show: boolean,
        freshBloodOverride: boolean
    },
    allowDuplicateXm: boolean,
    rexm: {
        fullyConfident: boolean,
        centerResponses: number
    },
    isNewlyAdded: boolean,
    isFullyConfident: boolean,
    actionIsOpen: boolean,
    mortalityRiskModal: boolean,
}

class EvaluateRexm extends React.Component<TProps, IState> {
    readonly state: IState = {
        recipient: null,
        donor: null,
        compatibilityResult: null,
        lkdpiResults: null,
        compatiblePairLkdpiResults: null,
        singleEpletMmRisk: null,
        isRecipientCenter: false,
        recipientCenterContacts: [],
        isDonorCenter: false,
        donorCenterContacts: [],
        donorNote: null,
        donorNotesContent: '',
        matchNote: null,
        matchNotesContent: '',
        donorKidneyPickupAddress: null,
        donorKidneyShipToAddress: null,
        recipientKidneyPickupAddress: null,
        recipientKidneyShipToAddress: null,
        serologyTestList: [],
        lookupData: {
            stateList: [],
            hardBlockReasons: []
        },
        doesStudyExist: 0,
        centerAddress: {
            show: false,
            data: null
        },
        showSerologyTests: false,
        hasOutstandingOffer: false,
        showInfoRequest: false,
        hasExistingXm: false,
        existingXmStatus: '',
        requestExm: {
            show: false,
            freshBloodOverride: false
        },
        allowDuplicateXm: true,
        rexm: {
            fullyConfident: null,
            centerResponses: 0
        },
        isNewlyAdded: null,
        isFullyConfident: false,
        actionIsOpen: false,
        mortalityRiskModal: false,
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        this.props.showLoading('Getting information, one moment please...').then(() => {
            this.props.getRexm(this.props.centerId, this.props.recipientId, this.props.rexmId).then(response => {
                if (response.data.success) {
                    let {
                        recipient, donor,
                        compatibilityResult, lkdpiResults, compatiblePairLkdpiResults, singleEpletMmRisk,
                        isRecipientCenter, recipientCenterContacts,
                        isDonorCenter, donorCenterContacts,
                        donorNote, matchNote,
                        donorKidneyPickupAddress, donorKidneyShipToAddress,
                        recipientKidneyPickupAddress, recipientKidneyShipToAddress,
                        serologyTestList,
                        doesStudyExist,
                        hasExistingXm, existingXmStatus,
                        hasOutstandingOffer,
                        rexm,
                        lookupData
                    } = response.data.results;

                    this.setState({
                        recipient,
                        donor,
                        compatibilityResult,
                        lkdpiResults,
                        compatiblePairLkdpiResults,
                        singleEpletMmRisk,
                        isRecipientCenter,
                        recipientCenterContacts,
                        isDonorCenter,
                        donorCenterContacts,
                        donorNote,
                        donorNotesContent: donorNote != null ? donorNote.note : '',
                        matchNote,
                        matchNotesContent: matchNote != null ? matchNote.note : '',
                        donorKidneyPickupAddress, donorKidneyShipToAddress,
                        recipientKidneyPickupAddress, recipientKidneyShipToAddress,
                        serologyTestList,
                        doesStudyExist,
                        lookupData,
                        hasExistingXm,
                        existingXmStatus,
                        hasOutstandingOffer,
                        rexm
                    }, () => {
                        this.props.hideLoading();

                        this.props.onUpdateRecipientAlias(this.state.recipient.alias);
                    });
                }
            });
        });
    }

    handleAcceptDonor = (isFullyConfident: boolean) => {
        let { id: recipientId, hasMortalityRisk } = this.state.recipient;
        let donorId = this.state.donor.id;

        this.props.showLoading('Updating preselect, one moment please...').then(() => {
            this.props.acceptDonor(recipientId, donorId, isFullyConfident, hasMortalityRisk).then(response => {
                this.props.hideLoading();

                if (response.data.success) {
                    this.getData();

                    this.setState({
                        isNewlyAdded: response.data.results == 1,
                        isFullyConfident
                    });

                    toast.success('Preselect completed successfully: Accepted ' + (isFullyConfident ? 'Fully Confident' : 'Not Fully Confident'));
                }
            });
        });
    }

    handleDeclineDonor = () => {
        let { id: recipientId, centerId: recipientCenterId, hasMortalityRisk } = this.state.recipient;
        let donorId = this.state.donor.id;

        this.props.showLoading('Updating preselect, one moment please...').then(() => {
            this.props.declineDonorRexm(recipientId, recipientCenterId, donorId, 'ReFR', 'Other', 'Declined donor at Request for Donor Review', false, hasMortalityRisk).then(response => {
                this.props.hideLoading();

                if (response.data.success) {
                    this.getData();
                    toast.success('Preselect completed successfully: Declined');
                }
            });
        });
    }

    handleShowRequestInfo = () => {
        this.setState({
            showInfoRequest: true
        });
    }

    handleRequestInfo = (message: string) => {
        let { recipient, donor } = this.state;

        this.setState({
            showInfoRequest: false
        }, () => {
            this.props.showLoading('Sending info request, one moment please...').then(() => {
                this.props.requestInfo(recipient.id, donor.id, message).then(response => {
                    this.props.hideLoading();

                    if (response.data.success) {
                        toast.success('Info request sent successfully');
                    }
                });
            });
        });
    }

    handleCancelRequestInfo = () => {
        this.setState({
            showInfoRequest: false
        });
    }

    handleShowRequestExploratoryXm = (freshBloodOverride: boolean) => {
        this.setState({
            requestExm: {
                show: true,
                freshBloodOverride
            }
        });
    }

    handleRequestExploratoryXm = () => {
        let { recipient, donor } = this.state;
        let allowDuplicateXm = this.state.allowDuplicateXm;
        let freshBloodOverride = this.state.requestExm.freshBloodOverride;

        this.setState({
            requestExm: {
                show: false,
                freshBloodOverride: false
            },
            allowDuplicateXm: false
        }, () => {
            this.props.showLoading('Sending exploratory XM request, one moment please...').then(() => {
                this.props.requestExm(recipient.id, donor.id, '', allowDuplicateXm, freshBloodOverride).then(response => {
                    this.props.hideLoading();

                    if (response.data.success) {
                        let { xmId, orderNumber } = response.data.results;

                        let message = orderNumber != '' ? 'EXM request sent successfully (' + xmId + '): PathTec Order ' + orderNumber : 'EXM request sent successfully (' + xmId + ')';

                        toast.success(message);
                    }
                });
            });
        });
    }

    handleCancelRequestExploratoryXm = () => {
        this.setState({
            requestExm: {
                show: false,
                freshBloodOverride: false
            },
        });
    }

    handleActionToggle = () => {
        this.setState({
            actionIsOpen: !this.state.actionIsOpen
        });
    }

    handleActionItem = (eventKey: any) => {
        this.setState({
            actionIsOpen: false
        }, () => {
            let { donor } = this.state;

            switch (eventKey) {
                case ActionItems.DonorHlaFile:
                    // window.open(__OUTSIDEURL__ + '/centers/download/donor-record.php?id=' + donor.id + '&type=rawtyping', '_blank');
                    this.props.onGetFile(FileDocumentType.DonorRawTyping, donor.id);
                    break;

                case ActionItems.Donor3GTypingReport:
                    // window.open(__OUTSIDEURL__ + '/centers/donor/histo-hla-file.php?id=' + donor.id, '_blank');
                    this.props.onGetFile(FileDocumentType.DonorHistoTyping, donor.id);
                    break;

                case ActionItems.VRLConfirmatoryTypingReport:
                    //window.open(__OUTSIDEURL__ + '/centers/download/donor-record.php?id=' + donor.id + '&type=vrlhla', '_blank');
                    this.props.onGetFile(FileDocumentType.DonorRawTyping, donor.id);
                    break;

                case ActionItems.ViewDonorCTStudy:
                    this.handleCtView(donor.id)
                    break;

                case ActionItems.DonorCTReport:
                    // window.open(__OUTSIDEURL__ + '/centers/download/donor-record.php?id=' + donor.id + '&type=ctreport', '_blank');
                    this.props.onGetFile(FileDocumentType.DonorCTReport, donor.id);
                    break;

                case ActionItems.RequestInfo:
                    this.handleShowRequestInfo();
                    break;

                case ActionItems.RequestExm:
                    this.handleShowRequestExploratoryXm(false);
                    break;

                case ActionItems.RequestExmFreshBlood:
                    this.handleShowRequestExploratoryXm(true);
                    break;
            }
        });
    }

    handleCtView = (donorId: number) => {
        this.props.getAmbraLink(donorId).then(response => {
            window.open(response.data.results, "_blank");
        });
    }

    handleGetFile = (docType: FileDocumentType, donorId: number) => {
        this.props.downloadFile(docType, donorId).then((response: any) => {
            downloadFile(response.data, `${FileDocumentTypeDescription[docType]}.pdf`, response.headers.contentType)
        }).catch((error: IBaseResponse) => {
            toast.error("Unable to download file at this time.  Please contact support.");
        })
    }

    handleDeclineDonorCheckMortality = () => {
        let { hasMortalityRisk } = this.state.recipient;

        if (hasMortalityRisk){
            this.setState({
                mortalityRiskModal: true
            })
        }
        else {
            this.handleDeclineDonor();
        }
    }

    handleDeclineMortalityRiskCancel = () => {
        this.setState({
            mortalityRiskModal: false
        })
    }

    handleRecipientWithMortalityRiskDecline = () => {
        this.setState({
            mortalityRiskModal: false
        }, () => {
            this.handleDeclineDonor();
        })
    }

    renderButtons = () => {
        let extra = null;

        if (this.state.compatibilityResult.donor.isAccepted != null) {
            if (this.state.compatibilityResult.donor.isAccepted == true) {
                extra = <div style={{ marginTop: 10 }}><strong>You previously accepted this donor.</strong></div>;
            } else {
                extra = <div style={{ marginTop: 10 }}><strong>You have already rejected this donor and do not need to do anything more unless that has changed.</strong></div>;
            }
        }

        return (
            <>
                <Alert bsStyle='info'>
                    <div>We have identified a donor for your hard to match patient.</div>
                    {extra}
                </Alert>

                <Row style={{ marginBottom: 15 }}>
                    <Col xs={12} sm={4} style={{ textAlign: 'center' }}>
                        <WellTopWrapper>
                            If the donor is acceptable and you are fully confident the cross match will be acceptable:
                        </WellTopWrapper>
                        <WellBottomWrapper>
                            <Button bsStyle='primary' bsSize='lg' block onClick={() => this.handleAcceptDonor(true)}>Accept Donor</Button>
                        </WellBottomWrapper>
                    </Col>
                    <Col xs={12} sm={3} style={{ textAlign: 'center' }}>
                        <WellTopWrapper>
                            If the donor is not acceptable then you should decline this donor:
                        </WellTopWrapper>
                        <WellBottomWrapper>
                            <Button bsStyle='danger' bsSize='lg' block onClick={this.handleDeclineDonorCheckMortality}>Decline Donor</Button>
                        </WellBottomWrapper>
                    </Col>
                </Row>
            </>
        );
    }

    render = () => {
        if (this.state.recipient == null || this.state.donor == null)
            return <div style={{ padding: '0px 5px' }}>{Utility.getWaitMessage()}...</div>;

        return (
            <>
                <div style={{ padding: '0px 5px' }}>
                    <RecipientNav
                        activeKey='EVALUATE-REXM'
                        isAdmin={false}
                        isNkrLogin={false}
                        centerId={this.props.centerId}
                        recipientId={this.props.recipientId}
                        recipientAlias={this.state.recipient.alias}
                        rexmId={this.props.rexmId}
                    />

                    <div style={{ width: "100%", minHeight: "65vh", paddingTop: 20, paddingLeft: 5, paddingRight: 5, marginBottom: 5, backgroundColor: "#FFF" }}>
                        <div className='container'>

                            <div className='row'>
                                <div className='col-xs-12'>
                                    <div className='row' style={{ marginBottom: 20 }}>
                                        <div className='col-xs-12 col-sm-8 col-print-12'>
                                            <h2 style={{ margin: 0 }}>Request for Review - {this.state.compatibilityResult.donor.alias} @ {this.state.compatibilityResult.donor.center.alias}</h2>
                                        </div>
                                        <div className='col-xs-12 col-sm-4 no-print'>
                                            <ActionsButton
                                                donor={this.state.compatibilityResult.donor}
                                                recipient={this.state.recipient}
                                                doesStudyExist={this.state.doesStudyExist}
                                                hasOutstandingOffer={false}
                                                isOpen={this.state.actionIsOpen}
                                                showMatchDecisionOptions={false}
                                                isNkrLogin={this.props.app.user.group.isSuper}
                                                matchHistoryId={0}
                                                onActionToggle={this.handleActionToggle}
                                                onActionItem={this.handleActionItem}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {this.state.isNewlyAdded && this.state.isFullyConfident && (
                                <Alert bsStyle='success'>
                                    You have successfully recorded that you are fully-confident this cross match will be acceptable.
                                    <br />
                                    Based on your decision, we have automatically created/updated the pre-selects for this donor.
                                    <br />
                                    If this is correct, there is no need to request an exploratory XM at this time.
                                </Alert>
                            )}

                            {this.state.isNewlyAdded && this.state.recipient.cpra4 >= 99 && this.state.isFullyConfident == false && (
                                <Alert bsStyle='warning'>
                                    You have successfully recorded that you are NOT fully-confident this cross match will be acceptable.
                                    <br />
                                    Based on your decision, we have automatically created/updated the pre-selects for this donor.
                                    <br />
                                    An exploratory XM was requested automatically.
                                </Alert>
                            )}

                            {this.state.isNewlyAdded && this.state.recipient.cpra4 < 99 && this.state.isFullyConfident == false && (
                                <Alert bsStyle='warning'>
                                    You have successfully recorded that you are NOT fully-confident this cross match will be acceptable.
                                    <br />
                                    Based on your decision, we have automatically created/updated the pre-selects for this donor.
                                    <br />
                                    An exploratory XM was not requested automatically. Please reach out to the donor center and ask permission. If its ok, please email NKR Logistics and they will order the kit.
                                </Alert>
                            )}

                            {this.renderButtons()}
                        </div>

                        <Match
                            donor={this.state.donor}
                            recipient={this.state.recipient}
                            compatibilityResult={this.state.compatibilityResult}
                            lkdpiResults={this.state.lkdpiResults}
                            compatiblePairLkdpiResults={this.state.compatiblePairLkdpiResults}
                            singleEpletMmRisk={this.state.singleEpletMmRisk}
                            donorNote={this.state.donorNote}
                            donorNotesContent={this.state.donorNotesContent}
                            matchNote={this.state.matchNote}
                            matchNotesContent={this.state.matchNotesContent}
                            includeButtons={false}
                            doesStudyExist={this.state.doesStudyExist}
                            hasOutstandingOffer={this.state.hasOutstandingOffer}
                            isRecipientCenter={this.state.isRecipientCenter}
                            showNotes={false}
                            isNkrLogin={this.props.app.user.group.isSuper}
                            swapNumber={''}
                            matchHistoryId={0}
                            onDonorNotesChange={() => null}
                            onMatchNodesChange={() => null}
                            onSaveNotes={() => null}
                            onAcceptDonor={() => null}
                            onDeclineDonor={() => null}
                            onRequestInfo={() => null}
                            onRequestExploratoryXm={() => null}
                            onGetFile={this.handleGetFile}
                            onViewCtScan={this.handleCtView}
                        />
                    </div>
                </div>

                <InfoRequest
                    show={this.state.showInfoRequest}
                    onConfirmRequest={this.handleRequestInfo}
                    onCancel={this.handleCancelRequestInfo}
                />

                <ExmRequest
                    show={this.state.requestExm.show}
                    hasExistingXm={this.state.hasExistingXm}
                    existingXmStaus={this.state.existingXmStatus}
                    onConfirmRequest={this.handleRequestExploratoryXm}
                    onCancel={this.handleCancelRequestExploratoryXm}
                />

                <DeclineDonorForMortalityRisk
                    show={this.state.mortalityRiskModal}
                    alias={this.state.compatibilityResult.donor.alias}
                    centerAlias={this.state.compatibilityResult.donor.center.alias}
                    riskPercent={this.state.recipient.mortalityRiskPercent}
                    onDeclineDonor={this.handleRecipientWithMortalityRiskDecline}
                    onCancel={this.handleDeclineMortalityRiskCancel}
                />
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        app: appState.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getRexm: (centerId, recipientId, rexmId) => {
            return dispatch(MatchActionCreators.getRexm(centerId, recipientId, rexmId));
        },
        acceptDonor: (recipientId, donorId, isFullyConfident, hasMortalityRisk) => {
            return dispatch(DonorPreselectActionCreators.acceptDonor(recipientId, donorId, isFullyConfident, hasMortalityRisk, MortalityMatchType.Rexm));
        },
        declineDonorRexm: (recipientId, recipientCenterId, donorId, hardBlockSource, hardBlockReason, hardBlockComments, isGlobalDecline, hasMortalityRisk) => {
            return dispatch(DonorPreselectActionCreators.declineDonorRexm(recipientId, recipientCenterId, donorId, hardBlockSource, hardBlockReason, hardBlockComments, isGlobalDecline, hasMortalityRisk, MortalityMatchType.Rexm));
        },
        requestInfo: (recipientId, donorId, message) => {
            return dispatch(DonorPreselectActionCreators.requestInfo(recipientId, donorId, message));
        },
        requestExm: (recipientId, donorId, message, allowDuplicate, freshBloodOverride) => {
            return dispatch(DonorPreselectActionCreators.requestExm(recipientId, donorId, message, allowDuplicate, freshBloodOverride));
        },
        downloadFile: (docType, id) => {
            return dispatch(FileActionCreators.downloadFile(docType, id))
        },
        getAmbraLink: (donorId) => {
            return dispatch(DonorActionCreators.getAmbraLink(donorId));
        },
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(EvaluateRexm)